/*! *********************************************************************
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _size from 'lodash/size';
import _get from 'lodash/get';
import _some from 'lodash/some';
import _slice from 'lodash/slice';
import _isArray from 'lodash/isArray';
import _reduce from 'lodash/reduce';
import _map from 'lodash/map';
import _floor from 'lodash/floor';
import _remove from 'lodash/remove';
import Joi from '@hapi/joi';
import RawAsset, { AssetStatus, AttachmentTypename, PreviewAttachmentKind, } from './RawAsset';
/**
 * Actions of a Substance Asset
 */
export var AssetActions;
(function (AssetActions) {
    AssetActions["PUBLISH"] = "publish";
    AssetActions["DELETE"] = "delete";
    AssetActions["ARCHIVE"] = "archive";
    AssetActions["EDIT"] = "edit";
})(AssetActions || (AssetActions = {}));
/**
 * A Substance Asset
 */
var Asset = /** @class */ (function (_super) {
    __extends(Asset, _super);
    function Asset(rawAsset) {
        var _this = _super.call(this, rawAsset) || this;
        _this.formats = ['sbsar'];
        _this.defaultFormat = 'sbsar';
        _this._downloads = {};
        _this._hasRevisions = null;
        return _this;
    }
    Object.defineProperty(Asset.prototype, "downloadableFormats", {
        /**
         * Returns list of avialable formats to download
         * @return {string[] | null} array of available formats to download
         */
        get: function () {
            var availableFormats = Object.keys(this._downloads);
            if (availableFormats.length > 0)
                return availableFormats;
            else
                return Object.keys(this.downloads);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "downloads", {
        /**
         * Return a list of download attachments
         * @returns {DownloadsInterface} array of downloads attachments
         */
        get: function () {
            var _this = this;
            if (!this.defaultFormat ||
                this.defaultFormat === '' ||
                !this.formats.includes(this.defaultFormat)) {
                throw Error('INVALID_DOWNLOAD_DEFAULTS');
            }
            if (Object.keys(this._downloads).length > 0)
                return this._downloads;
            this._downloads = _reduce(this.formats, function (result, format) {
                var attachment = _this.findAttachment({
                    __typename: AttachmentTypename.DOWNLOAD,
                    tag: format,
                });
                if (attachment) {
                    result[format] = attachment;
                }
                return result;
            }, {});
            return this._downloads;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "defaultDownload", {
        get: function () {
            var foundAttachment = this.findAttachment({
                __typename: AttachmentTypename.DOWNLOAD,
                tag: this.defaultFormat,
            });
            return foundAttachment;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "canBeDisplayed", {
        /**
         * Check if the asset have enough information to be displayed
         * @return {boolean} True if the asset can be displayed
         */
        get: function () {
            return !_isEmpty(this.id) && !_isEmpty(this.title);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "isPending", {
        /**
         * Check if the asset status is STATUS.PENDING
         * @return {boolean} True if the asset status is STATUS.PENDING
         */
        get: function () {
            return this.status === AssetStatus.PENDING;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "isInvalid", {
        /**
         * Check if the asset status is STATUS.INVALID
         * @return {boolean} True if the asset status is STATUS.INVALID
         */
        get: function () {
            return this.status === AssetStatus.INVALID;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "isDraft", {
        /**
         * Check if the asset status is STATUS.DRAFT
         * @return {boolean} True if the asset status is STATUS.DRAFT
         */
        get: function () {
            return this.status === AssetStatus.DRAFT;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "isPublished", {
        /**
         * Check if the asset status is STATUS.PUBLISHED
         * @return {boolean} True if the asset status is STATUS.PUBLISHED
         */
        get: function () {
            return this.status === AssetStatus.PUBLISHED;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "isArchived", {
        /**
         * Check if the asset status is STATUS.ARCHIVED
         * @return {boolean} True if the asset status is STATUS.ARCHIVED
         */
        get: function () {
            return this.status === AssetStatus.ARCHIVED;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "hasRevisions", {
        /**
         * Check if at least one of download attachments of the asset have more than one revision.
         * @return {boolean} True if there is revisions for one a download attachments.
         */
        get: function () {
            var _this = this;
            var result = false;
            if (this._hasRevisions === null) {
                result = Object.keys(this._downloads).some(function (format) {
                    var revisions = _this._downloads[format].revisions;
                    return Array.isArray(revisions) && revisions.length > 1;
                });
            }
            else
                result = this._hasRevisions;
            return result;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Return attachments that matches params
     * @param {AssetAttachmentFilter} args object that can have at least `__typename`, `label, `tag`, `kind`
     * @return {AssetAttachment[] | null}
     */
    Asset.prototype.findAttachments = function (_a) {
        var __typename = _a.__typename, label = _a.label, tag = _a.tag, kind = _a.kind;
        var result = null;
        if (_isArray(this.attachments)) {
            result = this.attachments;
            if (__typename)
                result = _filter(result, function (attachment) { return attachment.__typename === __typename; });
            if (label)
                result = _filter(result, { label: label });
            if (tag)
                result = _filter(result, function (a) {
                    return _includes(a.tags, tag);
                });
            if (kind)
                result = _filter(result, function (attachment) {
                    return attachment.__typename === AttachmentTypename.PREVIEW &&
                        attachment.kind === kind;
                });
            if (_size(result) === 0)
                result = null;
        }
        return result;
    };
    /**
     * Return the first attachment that matches params
     * @param {AssetAttachmentFilter} args object that can have at least `__typenname`, `label, `tag`, `kind`
     * @return {AssetAttachment | null}
     */
    Asset.prototype.findAttachment = function (_a) {
        var __typename = _a.__typename, label = _a.label, tag = _a.tag, kind = _a.kind;
        var candidates = this.findAttachments({ __typename: __typename, label: label, tag: tag, kind: kind });
        return _get(candidates, "[0]", null);
    };
    Object.defineProperty(Asset.prototype, "mainPreview", {
        /**
         * Getter for the main preview
         * @return {PreviewAttachment | null} PreviewAttachment with tag main or null
         */
        get: function () {
            return (this.thumbnail ||
                this.findAttachment({
                    __typename: AttachmentTypename.PREVIEW,
                    kind: PreviewAttachmentKind.IMAGE,
                    tag: 'main',
                }));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "homePreview", {
        /**
         * Get the preview that should be used on the homepage
         */
        get: function () {
            return this.mainPreview;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "previews", {
        /**
         * Return all the PreviewAttachment
         * @return {PreviewAttachment[] | null}
         */
        get: function () {
            var previews = this.findAttachments({
                __typename: AttachmentTypename.PREVIEW,
                kind: PreviewAttachmentKind.IMAGE,
            }) || [];
            return _remove(previews, function (a) { return a.tags.indexOf('closeup') === -1; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "presetPreviews", {
        /**
         * Return the presets
         * @return {PreviewAttachment[] | null} array of PreviewAttachment with tag preset or null
         */
        get: function () {
            return this.findAttachments({
                __typename: AttachmentTypename.PREVIEW,
                tag: 'preset',
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "additionnalPreviews", {
        /**
         * Return the additional previews
         * @return {PreviewAttachment[] | null} array of PreviewAttachment with tag additional or null
         */
        get: function () {
            return this.findAttachments({
                __typename: AttachmentTypename.PREVIEW,
                kind: PreviewAttachmentKind.IMAGE,
                tag: 'additional',
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "farPreview", {
        /**
         * Return the far preview
         * @return {PreviewAttachment | null} PreviewAttachment with tag far or null
         */
        get: function () {
            return this.findAttachment({
                __typename: AttachmentTypename.PREVIEW,
                kind: PreviewAttachmentKind.IMAGE,
                tag: 'far',
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "slicesPreview", {
        /**
         * Return the slices preview
         * @return {PreviewAttachment | null} PreviewAttachment with tag slices or null
         */
        get: function () {
            return this.findAttachment({
                __typename: AttachmentTypename.PREVIEW,
                kind: PreviewAttachmentKind.IMAGE,
                tag: 'slices',
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "triPreview", {
        /**
         * Return the tri preview
         * @return {PreviewAttachment || null} PreviewAttachment with tag tri or null
         */
        get: function () {
            return this.findAttachment({
                __typename: AttachmentTypename.PREVIEW,
                kind: PreviewAttachmentKind.IMAGE,
                tag: 'tri',
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "presets", {
        /**
         * Return max 3 preset previews
         * @return {PreviewAttachment[] | null} array of PreviewAttachment with tag additional or null
         */
        get: function () {
            var result = _slice(this.presetPreviews, 0, 3);
            if (_isEmpty(result))
                result = null;
            return result;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Asset.prototype, "isPreviewAutogen", {
        /**
         * Return true if the main preview is auto generated
         * @return {boolean}
         */
        get: function () {
            var search = 'autogen';
            return _some(_get(this, 'preview.tags'), function (tag) { return tag === search; });
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Get an extra data value
     * @param  {string} key
     * @return {string | null} null if not found
     */
    Asset.prototype.getExtraData = function (key) {
        var result = _filter(this.extraData, function (data) { return data.key === key; });
        return _get(result, '[0].value', null);
    };
    /**
     * Create a PreviewAttachment with a file in it.
     * Brings consistency across upload and edition.
     * @param {File} file the file that should be uploaded
     */
    Asset.prototype.addPreviewAttachmentFromFile = function (file) {
        if (_isArray(this.attachments)) {
            var fileUrl = URL.createObjectURL(file);
            var time = Date.now();
            var now = new Date(time);
            var attachment = {
                id: fileUrl,
                createdAt: now.toISOString(),
                file: file,
                kind: PreviewAttachmentKind.IMAGE,
                label: file.name,
                tags: ['preview', 'additional'],
                url: fileUrl,
                __typename: AttachmentTypename.PREVIEW,
                weight: this.additionnalPreviews
                    ? this.additionnalPreviews.length + 1
                    : 1,
            };
            this.attachments.push(attachment);
        }
    };
    /**
     * Create a DownloadAttachment with a file in it.
     * Brings consistency across upload and edition.
     * @param {File} file the file that should be uploaded
     */
    Asset.prototype.addDownloadAttachmentFromFile = function (file) {
        if (_isArray(this.attachments)) {
            var fileUrl = URL.createObjectURL(file);
            var time = Date.now();
            var now = new Date(time);
            var attachment = {
                id: fileUrl,
                createdAt: now.toISOString(),
                file: file,
                filename: file.name,
                mimetype: file.type,
                size: file.size,
                tags: ['default'],
                label: file.name,
                url: fileUrl,
                revisions: [
                    {
                        filename: file.name,
                        mimetype: file.type,
                        size: file.size,
                        createdAt: now.toISOString(),
                        revision: 0,
                    },
                ],
                __typename: AttachmentTypename.DOWNLOAD,
            };
            this.attachments.push(attachment);
        }
    };
    Object.defineProperty(Asset.prototype, "likes", {
        /**
         * Get asset likes
         * @return {number} number of likes. -1 if there is no stats.
         */
        get: function () {
            return _get(this, 'stats.likes', -1);
        },
        /**
         * Set asset likes
         */
        set: function (count) {
            if (this.stats)
                this.stats.likes = count;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Validate current asset
     * @return true if valid, object with error details if not
     */
    Asset.prototype.isValid = function (categories) {
        var _a;
        if (categories === void 0) { categories = []; }
        var schema = Joi.object({
            id: Joi.string().required().min(1),
            title: Joi.string().required().min(4),
            description: Joi.string().required(),
            tags: Joi.array().items(Joi.string(), Joi.string().insensitive()),
            categories: Joi.array()
                .items(Joi.string(), (_a = Joi.string())
                .valid.apply(_a, __spreadArray([], __read(categories), false)).insensitive()
                .required())
                .min(1)
                .required(),
            defaultFormat: Joi.string().required(),
            formats: Joi.array()
                .items(Joi.string().valid(this.defaultFormat).required, Joi.string())
                .min(1)
                .required(),
            defaultDownload: Joi.object({
                id: Joi.string().required().min(1),
                url: Joi.string().uri().required(),
                tags: Joi.array()
                    .items(Joi.string().valid(this.defaultFormat).required(), Joi.string())
                    .min(1)
                    .required(),
            })
                .unknown()
                .required(),
            mainPreview: Joi.object({
                id: Joi.string().required().min(1),
                url: Joi.string().uri().required(),
                tags: Joi.array()
                    .items(Joi.string().valid('placeholder').forbidden(), Joi.string().valid('thumbnail').required(), Joi.string().valid('main').required(), Joi.string())
                    .min(1)
                    .required(),
            })
                .unknown()
                .required(),
        });
        var res = schema.validate({
            id: this.id,
            title: this.title,
            description: this.description,
            tags: this.tags,
            categories: this.categories,
            defaultFormat: this.defaultFormat,
            formats: this.formats,
            defaultDownload: this.defaultDownload,
            mainPreview: this.mainPreview,
        }, { abortEarly: false });
        if (_size(res.error) > 0)
            return {
                message: 'Validation failed',
                error: res.error,
                value: res.value,
            };
        else
            return true;
    };
    /**
     * Validate current draft asset
     * @return true if valid, object with error details if not
     */
    Asset.prototype.isDraftValid = function () {
        var schema = Joi.object({
            id: Joi.string().required().min(1),
            title: Joi.string().required().min(4),
        });
        var res = schema.validate({ id: this.id, title: this.title }, { abortEarly: false });
        if (_size(res.error) > 0)
            return {
                message: 'Validation failed',
                error: res.error,
                value: res.value,
            };
        else
            return true;
    };
    Object.defineProperty(Asset.prototype, "physicalSizes", {
        /**
         * Get physical size
         * @returns {x:integer,y:integer} values in meters
         */
        get: function () {
            var physicalSize = this.getExtraData('physicalSize');
            if (!physicalSize)
                return null;
            var sizes = _map(physicalSize.split('/'), function (s) {
                var size = parseInt(s, 10);
                // sizes in substances are in centimeters
                return _floor(size / 100, 2);
            });
            return __assign({ x: sizes[0], y: sizes[1] }, (sizes[2] > 0 && { z: sizes[2] }));
        },
        enumerable: false,
        configurable: true
    });
    return Asset;
}(RawAsset));
export default Asset;
